import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import CourseReelPlayer from '../components/CourseReelPlayer';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function CourseReel() {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Resume logic - check localStorage for last viewed index
  const resumeKey = `playlist_${playlistId}_progress`;

  const fetchPlaylist = async () => {
    try {
      const response = await api.get(`/reel-playlists/${playlistId}`);
      setPlaylist(response.data);
      setBlocks(response.data.blocks || []);
      
      // Check for resume progress
      const savedProgress = localStorage.getItem(resumeKey);
      if (savedProgress) {
        const index = parseInt(savedProgress, 10);
        if (index > 0 && index < response.data.blocks.length) {
          setCurrentIndex(index);
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch playlist:', err);
      setError(err.response?.data?.detail || 'Failed to load playlist');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaylist();
  }, [playlistId]);

  const handleNext = useCallback(() => {
    if (currentIndex < blocks.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      localStorage.setItem(resumeKey, nextIndex.toString());
    }
  }, [currentIndex, blocks.length, resumeKey]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      setCurrentIndex(prevIndex);
      localStorage.setItem(resumeKey, prevIndex.toString());
    }
  }, [currentIndex, resumeKey]);

  const handleSeek = useCallback((index) => {
    if (index >= 0 && index < blocks.length) {
      setCurrentIndex(index);
      localStorage.setItem(resumeKey, index.toString());
    }
  }, [blocks.length, resumeKey]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-black">
        <div className="text-white text-xl">Loading playlist...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-black text-white p-8">
        <h2 className="text-2xl font-bold mb-4">Playlist Niet Gevonden</h2>
        <p className="text-gray-400 mb-8">{error}</p>
        <Button onClick={() => navigate('/reels')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Terug naar Reels
        </Button>
      </div>
    );
  }

  if (blocks.length === 0) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-black text-white p-8">
        <h2 className="text-2xl font-bold mb-4">Geen Content</h2>
        <p className="text-gray-400 mb-8">Deze playlist heeft geen blocks.</p>
        <Button onClick={() => navigate('/reels')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Terug naar Reels
        </Button>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-black">
      {/* Back button - with safe area */}
      <div 
        className="absolute left-4 z-50"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
      >
        <Button
          size="icon"
          variant="ghost"
          className="rounded-full bg-black/30 text-white hover:bg-black/50 backdrop-blur-sm"
          onClick={() => navigate('/reels')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      </div>

      {/* Course badge + Progress - with safe area */}
      <div 
        className="absolute right-4 z-50 flex flex-col items-end gap-2"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
      >
        <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm">
          Course
        </div>
        <div className="bg-black/30 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
          {currentIndex + 1} / {blocks.length}
        </div>
      </div>

      {/* Player */}
      <CourseReelPlayer
        playlist={playlist}
        blocks={blocks}
        currentIndex={currentIndex}
        onNext={handleNext}
        onPrev={handlePrev}
        onSeek={handleSeek}
      />
    </div>
  );
}
